import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { exchangeBingToken, saveBingSites } from "../../../services/socialTokenService";
import SitePicker from "../../site-connections/components/SitePicker";
import { bingProvider, bingSitePicker } from "../bingProvider";
import { BingSite } from "../types/bing.types";

const BING_WEBMASTER_URL = "https://www.bing.com/webmasters";

/**
 * A 17-second screen recording of the steps below, from Bing Webmaster Tools
 * (public/guides). The key in it was deleted after recording and is blurred.
 */
const BING_KEY_GUIDE_GIF: string | null = "/guides/bing-api-key.gif";

type Phase = "key" | "pick" | "no_sites" | "updated";

interface BingKeyDialogProps {
  customerId: string;
  idToken: string;
  /**
   * The Brand's connected Bing sites. Empty: connect (check the key, then pick
   * sites). Otherwise "Update key": the new key must cover all of them, and is
   * saved for exactly them.
   */
  connectedSiteUrls: string[];
  /**
   * The site URLs saved with a key while the dialog was open (empty when nothing
   * was), so the page can show them as connected straight away: connected_profiles
   * is updated by an event, and reading it back at once can still miss them.
   */
  onClose: (savedSiteUrls: string[]) => void;
}

/**
 * Connect Bing Webmaster Tools with the owner's API key (SGL-134).
 *
 * The key lives only in this component's state: never in storage, never in a URL,
 * never shown again once saved. Closing the dialog unmounts it and the key with it.
 */
const BingKeyDialog: React.FC<BingKeyDialogProps> = ({
  customerId,
  idToken,
  connectedSiteUrls,
  onClose,
}) => {
  const updating = connectedSiteUrls.length > 0;
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [phase, setPhase] = useState<Phase>("key");
  const [sites, setSites] = useState<BingSite[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  // Sites a save has confirmed, and saves still in flight. Refs, not state: close()
  // must see them as they are now, not as of the last render.
  const savedUrls = useRef<Set<string>>(new Set());
  const pendingSaves = useRef<Set<Promise<unknown>>>(new Set());
  const titleId = useId();
  const inputId = useId();
  const errorId = useId();
  const noteId = useId();

  const picker = useMemo(() => {
    const base = bingSitePicker(apiKey);
    // The picker connects each site through save: record the ones that succeed, and
    // keep each save visible to close() until it settles.
    return {
      ...base,
      save: (cid: string, urls: string[], token: string) => {
        const saving = base.save(cid, urls, token).then((result) => {
          urls.forEach((url) => savedUrls.current.add(url));
          return result;
        });
        pendingSaves.current.add(saving);
        const settle = () => pendingSaves.current.delete(saving);
        saving.then(settle, settle);
        return saving;
      },
    };
  }, [apiKey]);

  // Done / Cancel / Escape / the backdrop: a site save still in flight is waited
  // for, so a site that does connect is never left off the page.
  const close = async () => {
    if (busy || closing) return;
    setClosing(true);
    await Promise.allSettled(Array.from(pendingSaves.current));
    onClose(Array.from(savedUrls.current));
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") void closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!apiKey.trim()) {
      setError("Paste your Bing API key.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await exchangeBingToken(apiKey, customerId, idToken);
      if (!updating) {
        setSites(result.sites);
        setPhase(result.sites.length ? "pick" : "no_sites");
        return;
      }
      const covered = new Set(result.sites.map((s) => s.site_url));
      const missing = connectedSiteUrls.filter((url) => !covered.has(url));
      if (missing.length) {
        setError(
          `This key doesn't cover ${missing.join(", ")}. Use the key from the Bing account where ${
            missing.length === 1 ? "that site is" : "those sites are"
          } verified.`
        );
        return;
      }
      await saveBingSites(customerId, connectedSiteUrls, idToken, apiKey);
      connectedSiteUrls.forEach((url) => savedUrls.current.add(url));
      setApiKey("");
      setPhase("updated");
    } catch (err: any) {
      setError(err?.message || "We couldn't check your Bing key. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (phase === "pick") {
    body = (
      <SitePicker provider={picker} sites={sites} customerId={customerId} onClose={() => void close()} />
    );
  } else if (phase === "no_sites") {
    body = (
      <>
        <h2 id={titleId} className="pr-10 text-xl font-semibold text-gray-800">
          No verified sites on this key
        </h2>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-gray-600">
          The key works, but its Bing account has no verified sites in Bing Webmaster Tools. If
          your site is already in Google Search Console, Bing Webmaster Tools can import it in one
          step. Once it shows as verified there, connect again.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setPhase("key");
              setApiKey("");
            }}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Use another key
          </button>
          <button
            type="button"
            onClick={() => void close()}
            className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
          >
            Done
          </button>
        </div>
      </>
    );
  } else if (phase === "updated") {
    body = (
      <>
        <h2 id={titleId} className="flex items-center gap-2 pr-10 text-xl font-semibold text-gray-800">
          <span className="material-icons text-[#008373]" aria-hidden>
            check_circle
          </span>
          Bing key updated
        </h2>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-gray-600">
          Your Bing data now comes in with the new key. We'll remind you when it's time to refresh
          it again. If you use the old key anywhere else, update it there too: Bing stops accepting
          it once you generate a new one.
        </p>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => void close()}
            className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900"
          >
            Done
          </button>
        </div>
      </>
    );
  } else {
    body = (
      <form onSubmit={submit} noValidate>
        <h2 id={titleId} className="pr-10 text-xl font-semibold text-gray-800">
          {updating ? "Update your Bing API key" : `Connect ${bingProvider.label}`}
        </h2>
        <p className="mt-1 max-w-prose text-sm text-gray-500">
          {updating
            ? `Paste a new key for ${connectedSiteUrls.join(", ")}. We'll check it with Bing before replacing the old one.`
            : "Bing gives you a key that lets us read your search data. It takes about a minute."}
        </p>

        <ol className="mt-5 space-y-3 text-sm text-gray-700">
          {[
            <>
              Open{" "}
              <a
                href={BING_WEBMASTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#00695c] underline decoration-[#008373]/40 underline-offset-2 hover:decoration-[#008373]"
              >
                Bing Webmaster Tools
              </a>{" "}
              and sign in with the account your site is verified in.
            </>,
            <>
              Click <b className="font-semibold text-gray-900">Settings</b> at the top right, then{" "}
              <b className="font-semibold text-gray-900">API access</b>.
            </>,
            <>
              Under <b className="font-semibold text-gray-900">API Key</b>, click{" "}
              <b className="font-semibold text-gray-900">Generate API Key</b>
              {updating ? "" : " (or View API Key if you already have one)"}, then copy it.
            </>,
            <>Paste it below.</>,
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#008373]/10 text-xs font-bold tabular-nums text-[#00695c]"
                aria-hidden
              >
                {i + 1}
              </span>
              <span className="pt-0.5 leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        {BING_KEY_GUIDE_GIF && (
          <img
            src={BING_KEY_GUIDE_GIF}
            alt="Bing Webmaster Tools: Settings, then API access, then Generate API Key, then Copy"
            width={960}
            height={479}
            className="mt-5 h-auto w-full rounded-lg border border-gray-200"
          />
        )}

        <label htmlFor={inputId} className="mt-6 block text-sm font-semibold text-gray-800">
          Bing API key
        </label>
        <div className="relative mt-1.5">
          <input
            id={inputId}
            name="bing-api-key"
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            autoFocus
            disabled={busy}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${errorId} ${noteId}` : noteId}
            placeholder="Paste your key"
            className={`w-full rounded-lg border bg-white py-2.5 pl-3 pr-11 font-mono text-sm text-gray-900 placeholder:font-sans placeholder:text-gray-500 focus:outline-none focus:ring-2 disabled:bg-gray-50 ${
              error
                ? "border-red-400 focus:border-red-500 focus:ring-red-500/25"
                : "border-gray-300 focus:border-[#008373] focus:ring-[#008373]/25"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            aria-label={showKey ? "Hide key" : "Show key"}
            aria-pressed={showKey}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-gray-500 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#008373]/40"
          >
            <span className="material-icons text-[20px]" aria-hidden>
              {showKey ? "visibility_off" : "visibility"}
            </span>
          </button>
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-2 text-sm text-red-700">
            {error}
          </p>
        )}
        <p id={noteId} className="mt-2 text-xs leading-relaxed text-gray-500">
          We store your key encrypted and never show it again. Paste it only here, never in chat.
          {updating && " Generating a new key in Bing stops the old one working everywhere you use it."}
        </p>

        <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-600">
          Your site must be verified in Bing Webmaster Tools. If it's in Google Search Console, Bing
          can import it in one click.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void close()}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex min-w-[8.5rem] items-center justify-center gap-2 rounded-lg bg-[#008373] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#006b5e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008373]/40 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
          >
            {busy && (
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent"
                aria-hidden
              />
            )}
            {busy ? "Checking with Bing…" : updating ? "Update key" : "Check key"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={phase === "pick" ? undefined : titleId}
      aria-label={phase === "pick" ? `Connect ${bingProvider.label}` : undefined}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8"
      onClick={() => void close()}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl bg-white p-6 text-left shadow-xl sm:p-8 [&_header_h1]:pr-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => void close()}
          disabled={busy}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
        >
          <span className="material-icons" aria-hidden>
            close
          </span>
        </button>
        {body}
      </div>
    </div>
  );
};

export default BingKeyDialog;
